import { Button, CircularProgress, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyboardBackspace } from '@mui/icons-material';
import { AddressFormConfig, AddressModel, RouteList, UserAddressType, UserProfile } from 'types';
import { AddressForm, BackButton } from 'components';
import { FieldPath, useForm } from 'react-hook-form';
import { useUsers } from 'hooks';
import {
  addressFormConfigProps,
  defaultUserProfileAddressFormValues,
  userProfileDefaultAddressFormConfig,
  userProfileDefaultContactBillingFormConfig,
} from 'shared';

type Props = {
  formType: UserAddressType;
};

export const EditAddress = ({ formType }: Props) => {
  const navigate = useNavigate();
  const isBilling = formType === UserAddressType.billing;

  type Model = AddressModel<typeof isBilling extends true ? 'defaultBillingAddress' : 'defaultContactAddress'>;
  const formConfig = (
    isBilling ? userProfileDefaultContactBillingFormConfig : userProfileDefaultAddressFormConfig
  ) as AddressFormConfig<Model>;

  const { userProfileQuery, updateUserProfileQuery } = useUsers();
  const { data, isLoading } = userProfileQuery;
  const userProfile = data?.user;
  const {
    watch,
    control,
    formState: { errors },
    clearErrors,
    reset,
    trigger,
  } = useForm<Model>({ defaultValues: defaultUserProfileAddressFormValues });

  const onFieldChange = <F extends FieldPath<Model>>(fieldName: F) => {
    clearErrors(fieldName);
  };

  useEffect(() => {
    if (!isLoading && userProfile) {
      const profileAddress = addressFormConfigProps.reduce((acc, prop) => {
        const { field } = formConfig[prop];
        acc[field] = userProfile[field];
        return acc;
      }, {} as Model);

      reset(profileAddress);
    }
  }, [formConfig, isLoading, reset, userProfile]);

  const onSubmit = async () => {
    const isFormValid = await trigger();
    if (isFormValid) {
      await updateUserProfileQuery.mutateAsync({ ...userProfile, ...(watch() as UserProfile) });
      navigate(RouteList.Profile);
    }
  };

  return (
    <Paper sx={{ p: 5 }}>
      <Stack pb={5} alignItems="start">
        <BackButton
          history
          variant="text"
          startIcon={<KeyboardBackspace color="primary" />}
          sx={(theme) => ({ textTransform: 'none', maxHeight: theme.spacing(8) })}
        >
          Zurück, ohne zu speichern
        </BackButton>
        <Typography variant="h2">{isBilling ? 'Rechnungsadresse' : 'Firmenadresse'} ändern</Typography>
      </Stack>
      <Divider />
      <Stack pt={5} pb={10}>
        <Typography variant="subtitle1">Rechnungsadresse</Typography>
        {!isLoading ? (
          <AddressForm
            formConfig={formConfig}
            testId={`default-${isBilling ? 'billing' : 'contact'}-address-form`}
            errors={errors}
            control={control}
            onFieldChange={onFieldChange}
          />
        ) : (
          <Stack spacing={2} width={{ xs: 1, lg: 2 / 3 }}>
            {Object.keys(formConfig).map((key) => (
              <Skeleton key={key} variant="rectangular" height={56} animation="wave" role="progressbar" />
            ))}
          </Stack>
        )}
      </Stack>

      <Divider />

      <Stack pt={10} direction="row" spacing={5} justifyContent="flex-end">
        <BackButton history variant="outlined">
          Abbrechen
        </BackButton>
        <Button variant="contained" onClick={onSubmit}>
          {updateUserProfileQuery.isPending ? <CircularProgress color="inherit" size={20} /> : 'Speichern'}
        </Button>
      </Stack>
    </Paper>
  );
};
